import { useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    InputAdornment,
    Popover,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import { tokens } from "@/theme/tokens";
import CustomPagination from "@/components/Common/Shared/CustomePagination";

/* ─── theme tokens (kept local so this file is portable) ─── */
export const THEME = {
    primary: "var(--primary)",
    primary08: "rgba(55,48,163,0.08)",
    primaryBorder: "rgba(55,48,163,0.4)",
    surface: "#FFFFFF",
    headerBg: "#FAFAF7",
    hover: "rgba(55,48,163,0.04)",
    border: "#EBEBEB",
    divider: "#EEEEEE",
    textPrimary: "#1F1F1F",
    textSecondary: "#6B6B6B",
    textMuted: "#9E9E9E",
};

/* ─── popover styles ──────────────────────────────────────── */
const popoverPaper = {
    mt: 0.75,
    minWidth: 220,
    maxWidth: 300,
    border: `1px solid ${THEME.divider}`,
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    p: 0,
    overflow: "hidden",
};
const cfpHead = {
    px: 2,
    pt: 1.5,
    pb: 0.75,
    fontSize: "11px",
    fontWeight: 700,
    color: THEME.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
};
const cfpActions = {
    display: "flex",
    justifyContent: "flex-end",
    gap: 1,
    px: 2,
    py: 1.25,
    borderTop: `1px solid ${THEME.divider}`,
    bgcolor: THEME.headerBg,
};
const cfpSortBtn = {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 2,
    py: 1,
    fontSize: "12.5px",
    cursor: "pointer",
    color: THEME.textPrimary,
    borderRadius: 0,
    textTransform: "none",
    justifyContent: "flex-start",
    width: "100%",
    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
};
const chipBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.25,
    py: 0.4,
    borderRadius: "20px",
    fontSize: "11.5px",
    fontWeight: 600,
    cursor: "pointer",
    border: `1px solid ${THEME.border}`,
    mr: 0.5,
    mb: 0.5,
    transition: "all 0.15s",
    userSelect: "none",
};

/* ─── FilterHeaderCell ────────────────────────────────────── */
function FilterHeaderCell({ label, colKey, filterConfig, onApply, onClear, onSort, align = "left" }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [textVal, setTextVal] = useState("");
    const [checked, setChecked] = useState({});
    const [activeChips, setActiveChips] = useState({});
    const open = Boolean(anchorEl);
    const hasFilter =
        colKey &&
        (Object.values(checked).some(Boolean) ||
            Object.values(activeChips).some(Boolean) ||
            Boolean(textVal));

    const toggleCheck = (val) => setChecked((p) => ({ ...p, [val]: !p[val] }));
    const toggleChip = (val, radio) => {
        if (radio) setActiveChips({ [val]: !activeChips[val] });
        else setActiveChips((p) => ({ ...p, [val]: !p[val] }));
    };
    const handleClear = () => {
        setTextVal("");
        setChecked({});
        setActiveChips({});
        onClear && onClear(colKey);
    };
    const handleApply = () => {
        onApply &&
            onApply(colKey, {
                text: textVal,
                checked: Object.keys(checked).filter((k) => checked[k]),
                chips: Object.keys(activeChips).filter((k) => activeChips[k]),
            });
        setAnchorEl(null);
    };

    if (!colKey || !filterConfig) {
        return (
            <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
                {label}
            </Typography>
        );
    }

    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
            <Box
                component="button"
                type="button"
                onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    color: "inherit",
                    fontWeight: "inherit",
                    fontSize: "inherit",
                    letterSpacing: "inherit",
                    textTransform: "inherit",
                    fontFamily: "inherit",
                    borderRadius: "4px",
                    "&:hover": { color: THEME.primary },
                }}
            >
                {label}
                {hasFilter && (
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: THEME.primary, ml: 0.5 }} />
                )}
                <KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.7 }} />
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: popoverPaper }}
                onClick={(e) => e.stopPropagation()}
            >
                <Typography sx={cfpHead}>{filterConfig.heading || `Filter by ${label}`}</Typography>

                {filterConfig.text && (
                    <Box sx={{ px: 2, pb: 1.25 }}>
                        <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder={filterConfig.textPlaceholder || "Search…"}
                            value={textVal}
                            onChange={(e) => setTextVal(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 16, color: THEME.textMuted }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { fontSize: "12.5px", borderRadius: "6px" } }}
                        />
                    </Box>
                )}

                {filterConfig.checkItems && (
                    <Box sx={{ px: 1.5, pb: 1, maxHeight: 240, overflowY: "auto" }}>
                        {filterConfig.checkItems.map((item) => (
                            <Box key={item.value}>
                                {item.divider && <Divider sx={{ my: 0.5 }} />}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!!checked[item.value]}
                                            onChange={() => toggleCheck(item.value)}
                                            sx={{ p: 0.5, "&.Mui-checked": { color: THEME.primary } }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "12.5px" }}>
                                            {item.dot && (
                                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.dot }} />
                                            )}
                                            <span>{item.label}</span>
                                            {item.sub && (
                                                <Typography component="span" sx={{ ml: 0.5, color: THEME.textMuted, fontSize: "11px" }}>
                                                    {item.sub}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                    sx={{ mx: 0, width: "100%", "& .MuiFormControlLabel-label": { flex: 1 } }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {filterConfig.chips && (
                    <Box sx={{ px: 2, pb: 1.25, display: "flex", flexWrap: "wrap" }}>
                        {filterConfig.chips.map((c) => (
                            <Box
                                key={c.value}
                                component="span"
                                onClick={() => toggleChip(c.value, filterConfig.chipsRadio)}
                                sx={{
                                    ...chipBase,
                                    bgcolor: activeChips[c.value] ? THEME.primary08 : "background.paper",
                                    color: activeChips[c.value] ? THEME.primary : THEME.textPrimary,
                                    borderColor: activeChips[c.value] ? THEME.primary : THEME.border,
                                }}
                            >
                                {c.dot && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c.dot }} />}
                                {c.label}
                            </Box>
                        ))}
                    </Box>
                )}

                {filterConfig.sortOptions && (
                    <>
                        <Divider />
                        {filterConfig.sortOptions.map((s) => (
                            <Box
                                key={s.dir + s.label}
                                component="button"
                                type="button"
                                onClick={() => {
                                    onSort && onSort(colKey, s.dir);
                                    setAnchorEl(null);
                                }}
                                sx={{ ...cfpSortBtn, background: "none", border: "none", fontFamily: "inherit" }}
                            >
                                {s.dir === "asc" ? (
                                    <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                ) : (
                                    <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                )}
                                {s.label}
                            </Box>
                        ))}
                    </>
                )}

                <Box sx={cfpActions}>
                    <Button size="small" onClick={handleClear} sx={{ color: THEME.textSecondary, textTransform: "none" }}>
                        Clear
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleApply}
                        sx={{ bgcolor: THEME.primary, textTransform: "none", "&:hover": { bgcolor: tokens.color.primary90 } }}
                    >
                        Apply
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
}

FilterHeaderCell.propTypes = {
    label: PropTypes.node.isRequired,
    colKey: PropTypes.string,
    filterConfig: PropTypes.object,
    onApply: PropTypes.func,
    onClear: PropTypes.func,
    onSort: PropTypes.func,
    align: PropTypes.string,
};

/* ─── CommonTable ─────────────────────────────────────────── */
const CommonTable = ({
    columns = [],
    data,
    rows,
    rowKey = "id",
    getRowKey,
    loading = false,
    skeletonRows = 4,
    emptyState,
    onRowClick,
    toolbarLeft,
    toolbarRight,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onApplyFilter,
    onClearFilter,
    onSort,
    selectable = false,
    selectedRowKeys = [],
    selectAll = false,
    isIndeterminate = false,
    onSelectAll,
    onSelectRow,
}) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isMd = useMediaQuery(theme.breakpoints.down("md"));

    const tableData = useMemo(() => data ?? rows ?? [], [data, rows]);

    const visibleColumns = useMemo(
        () =>
            columns.filter((c) => {
                if (isXs && c.hideOn?.includes("xs")) return false;
                if (isMd && c.hideOn?.includes("md")) return false;
                return true;
            }),
        [columns, isXs, isMd]
    );

    const headerCellSx = {
        bgcolor: THEME.headerBg,
        borderBottom: `1px solid ${THEME.border}`,
        color: THEME.textSecondary,
        fontWeight: 700,
        fontSize: "14px",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        py: 1.5,
    };

    const bodyCellSx = {
        borderBottom: `1px solid ${THEME.divider}`,
        fontSize: "0.875rem",
        color: THEME.textPrimary,
        py: 1.5,
    };

    const usePagination = typeof totalCount === "number" && typeof onPageChange === "function";

    return (
        <Box
            sx={{
                bgcolor: THEME.surface,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                overflow: "hidden",
            }}
        >
            {(toolbarLeft || toolbarRight) && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${THEME.divider}`,
                        bgcolor: THEME.surface,
                        flexWrap: "wrap",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>{toolbarLeft}</Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>{toolbarRight}</Box>
                </Box>
            )}

            <TableContainer sx={{ bgcolor: THEME.surface }}>
                <Table sx={{ bgcolor: THEME.surface }}>
                    <TableHead>
                        <TableRow>
                            {selectable && (
                                <TableCell padding="checkbox"
                                    sx={{ ...headerCellSx, textTransform: "capitalize" }}>
                                    <Checkbox
                                        size="small"
                                        checked={selectAll}
                                        indeterminate={isIndeterminate}
                                        onChange={onSelectAll}
                                    />
                                </TableCell>
                            )}
                            {visibleColumns.map((col) => (
                                <TableCell
                                    key={col.key}
                                    align={col.align || "left"}
                                    sx={{ ...headerCellSx, ...(col.headerSx || {}), minWidth: col.minWidth, textTransform: "capitalize" }}
                                >
                                    <FilterHeaderCell
                                        label={col.label}
                                        colKey={col.filterable ? col.key : null}
                                        filterConfig={col.filterConfig}
                                        align={col.align}
                                        onApply={onApplyFilter}
                                        onClear={onClearFilter}
                                        onSort={onSort}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            Array.from({ length: skeletonRows }).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    {visibleColumns.map((col) => (
                                        <TableCell key={col.key} sx={bodyCellSx}>
                                            <Skeleton variant="text" width="80%" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : tableData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={visibleColumns.length} sx={{ ...bodyCellSx, borderBottom: "none" }}>
                                    <Box sx={{ py: 6, textAlign: "center" }}>
                                        {emptyState || (
                                            <Typography variant="body2" sx={{ color: THEME.textMuted }}>
                                                No data available.
                                            </Typography>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            tableData.map((row, rowIndex) => {
                                const key = getRowKey ? getRowKey(row, rowIndex) : row[rowKey] ?? rowIndex;
                                return (
                                    <TableRow
                                        key={key}
                                        hover
                                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                                        sx={{
                                            cursor: onRowClick ? "pointer" : "default",
                                            transition: "background-color 0.15s ease",
                                            "&:hover": { bgcolor: THEME.hover },
                                        }}
                                    >
                                        {selectable && (
                                            <TableCell padding="checkbox" sx={bodyCellSx}>
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedRowKeys.includes(key)}
                                                    onChange={() => onSelectRow(row)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </TableCell>
                                        )}
                                        {visibleColumns.map((col) => (
                                            <TableCell
                                                key={col.key}
                                                align={col.align || "left"}
                                                sx={{ ...bodyCellSx, ...(col.cellSx || {}) }}
                                            >
                                                {col.render ? col.render(row, rowIndex) : row[col.key] ?? "—"}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {usePagination && (
                <CustomPagination
                    count={totalCount}
                    page={page ?? 0}
                    rowsPerPage={rowsPerPage ?? 10}
                    onPageChange={onPageChange}
                />
            )}
        </Box>
    );
};

CommonTable.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array,
    rows: PropTypes.array,
    rowKey: PropTypes.string,
    getRowKey: PropTypes.func,
    loading: PropTypes.bool,
    skeletonRows: PropTypes.number,
    emptyState: PropTypes.node,
    onRowClick: PropTypes.func,
    toolbarLeft: PropTypes.node,
    toolbarRight: PropTypes.node,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    totalCount: PropTypes.number,
    onPageChange: PropTypes.func,
    onApplyFilter: PropTypes.func,
    onClearFilter: PropTypes.func,
    onSort: PropTypes.func,
};

export default CommonTable;